import React, { useState } from "react";
import { PublicShell } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const WEEKS = [
  ["Your numbers", "one real report that generates itself every morning"],
  ["Your selling", "AI on follow-ups, quotes, listings, enquiries"],
  ["Your margin", "costing, pricing, what to stop selling"],
  ["Your people", "the work that stops being done by hand"],
  ["Build week", "you ship your own system, live, with help"],
  ["Show and tell", "everyone presents what's running"],
];
const DETAILS = [
  "Six weeks live, one session a week plus a WhatsApp group",
  "Ten seats, application only",
  "Free — founding cohort",
  "Built with you during the six weeks, not explained at you",
];

const WHATSAPP_NUMBER = "+91 98XXX XXXXX"; // placeholder — replace with Shubham's number

export default function Apply() {
  const [f, setF] = useState({ name: "", email: "", phone: "", business_name: "", city: "", what_you_sell: "", revenue_band: "₹1–3 crore", number_you_wish_you_knew: "", week_without_you: "", website: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/applications", f); setDone(true); }
    catch { toast.error("Could not send — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <PublicShell>
      <section className="border-b border-rule">
        <div className="mx-auto max-w-[1040px] px-5 py-16 lg:px-8">
          <p className="kicker text-xs text-brass">Application only</p>
          <h1 className="mt-4 font-heading text-4xl font-bold text-ink sm:text-5xl">The Room</h1>
          <p className="mt-4 max-w-2xl text-lg text-bodytext">Six weeks. Ten owners. Free while I build the first cohort.</p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {DETAILS.map((d) => <li key={d} className="flex gap-2 text-bodytext"><span className="text-brass">—</span>{d}</li>)}
          </ul>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <div className="mb-4 lg:w-[140px] lg:flex-shrink-0"><span className="kicker text-xs text-brass">01</span><span className="kicker ml-3 text-xs text-mutedink lg:ml-0 lg:mt-1 lg:block">The six weeks</span></div>
          <ol className="flex-1 divide-y divide-rule border-y border-rule">
            {WEEKS.map(([h, d], i) => (
              <li key={h} className="flex gap-4 py-4">
                <span className="kicker text-sm text-brass">{String(i + 1).padStart(2, "0")}</span>
                <div><span className="font-heading font-semibold text-ink">{h}</span> <span className="text-bodytext">— {d}</span></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[680px] px-5 py-16 lg:px-8">
          {done ? (
            <div className="rounded-[3px] border border-rule bg-card p-8 text-center" data-testid="apply-done">
              <h2 className="font-heading text-2xl font-bold text-ink">Got it.</h2>
              <p className="mt-3 text-bodytext">I read every one myself and reply within two days.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-[3px] border border-rule bg-card p-6 sm:p-8" data-testid="apply-form">
              <h2 className="font-heading text-2xl font-bold text-ink">Apply</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div><Label className="text-xs">Name</Label><Input required value={f.name} onChange={set("name")} data-testid="apply-name" /></div>
                <div><Label className="text-xs">Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="apply-email" /></div>
                <div><Label className="text-xs">Phone (WhatsApp)</Label><Input required value={f.phone} onChange={set("phone")} data-testid="apply-phone" /></div>
                <div><Label className="text-xs">Business name</Label><Input value={f.business_name} onChange={set("business_name")} data-testid="apply-business" /></div>
                <div><Label className="text-xs">City</Label><Input value={f.city} onChange={set("city")} data-testid="apply-city" /></div>
                <div><Label className="text-xs">What do you sell?</Label><Input value={f.what_you_sell} onChange={set("what_you_sell")} data-testid="apply-sell" /></div>
              </div>
              <div className="mt-4">
                <Label className="text-xs">Roughly what does a year look like?</Label>
                <select value={f.revenue_band} onChange={set("revenue_band")} className="mt-1 flex h-9 w-full rounded-[3px] border border-input bg-background px-3 text-sm" data-testid="apply-revenue">
                  {["Under ₹1 crore", "₹1–3 crore", "₹3–5 crore", "Above ₹5 crore"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <Label className="text-xs">What's the one number about your business you wish you knew and don't?</Label>
                <textarea value={f.number_you_wish_you_knew} onChange={set("number_you_wish_you_knew")} rows={3} className="mt-1 w-full rounded-[3px] border border-input bg-background px-3 py-2 text-sm" data-testid="apply-number" />
              </div>
              <div className="mt-4">
                <Label className="text-xs">What would change if the business could run a week without you?</Label>
                <textarea value={f.week_without_you} onChange={set("week_without_you")} rows={3} className="mt-1 w-full rounded-[3px] border border-input bg-background px-3 py-2 text-sm" data-testid="apply-week" />
              </div>
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                <label>Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} data-testid="apply-honeypot" /></label>
              </div>
              <Button type="submit" disabled={busy} className="mt-6 w-full rounded-[3px] bg-brass py-3 text-paper hover:opacity-90" data-testid="apply-submit">
                {busy ? "Sending…" : "Send application"}
              </Button>
              <p className="mt-4 text-center text-sm text-mutedink" data-testid="apply-whatsapp">
                Prefer WhatsApp? Message me on <span className="font-semibold text-bodytext">{WHATSAPP_NUMBER}</span>
              </p>
            </form>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
